import React, { useState, useEffect } from 'react';
import { AdminService } from '../../services/AdminService';
import AnalyticsChart, { ChartData } from '../display/AnalyticsChart';

// System statistics interface
interface SystemStats {
  totalUsers: number;
  totalNotes: number;
  totalDisplays: number;
  activeDisplays: number;
  pendingModeration: number;
  usersLastWeek: number;
  notesLastWeek: number;
  displayActivity: {
    region: string;
    displayCount: number;
    noteCount: number;
    sessionCount: number;
  }[];
}

/**
 * AdminDashboard - Main dashboard component for system administrators
 * Shows system-wide statistics, charts, and key metrics
 */
const AdminDashboard: React.FC = () => {
  // State management
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch system statistics
  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const systemStats = await AdminService.getSystemStats();
      setStats(systemStats);
    } catch (err) {
      console.error('Error fetching system stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to load system statistics');
    } finally {
      setLoading(false);
    }
  };

  // Effect to fetch data on component mount
  useEffect(() => {
    fetchStats();
  }, []);

  // Prepare chart data for regional activity
  const getRegionalActivityChartData = (): ChartData | null => {
    if (!stats || !stats.displayActivity || stats.displayActivity.length === 0) {
      return null;
    }

    return {
      labels: stats.displayActivity.map(item => item.region),
      datasets: [
        {
          label: 'Displays',
          data: stats.displayActivity.map(item => item.displayCount),
          backgroundColor: 'rgba(75, 192, 192, 0.6)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
        },
        {
          label: 'Notes',
          data: stats.displayActivity.map(item => item.noteCount),
          backgroundColor: 'rgba(255, 99, 132, 0.6)',
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 1,
        },
        {
          label: 'Sessions',
          data: stats.displayActivity.map(item => item.sessionCount),
          backgroundColor: 'rgba(54, 162, 235, 0.6)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
        },
      ],
    };
  };

  // Prepare chart data for user/note growth
  const getGrowthChartData = (): ChartData | null => {
    if (!stats) return null;

    return {
      labels: ['Total Users', 'New Users (7d)', 'Total Notes', 'New Notes (7d)'],
      datasets: [
        {
          label: 'Platform Growth',
          data: [stats.totalUsers, stats.usersLastWeek, stats.totalNotes, stats.notesLastWeek],
          backgroundColor: [
            'rgba(255, 206, 86, 0.6)',
            'rgba(255, 206, 86, 0.4)',
            'rgba(153, 102, 255, 0.6)',
            'rgba(153, 102, 255, 0.4)',
          ],
          borderColor: [
            'rgba(255, 206, 86, 1)',
            'rgba(255, 206, 86, 0.8)',
            'rgba(153, 102, 255, 1)',
            'rgba(153, 102, 255, 0.8)',
          ],
          borderWidth: 1,
        },
      ],
    };
  };

  // Loading state
  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading system statistics...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-danger" role="alert">
          <h5>Error Loading Dashboard</h5>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchStats}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // No data state
  if (!stats) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-info" role="alert">
          No statistics data available.
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="h3 mb-1">Admin Dashboard</h1>
              <p className="text-muted mb-0">System overview and key metrics</p>
            </div>
            <button className="btn btn-outline-primary" onClick={fetchStats}>
              <i className="fas fa-sync-alt me-2"></i>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="row mb-4">
        <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
          <div className="card text-center h-100">
            <div className="card-body">
              <div className="display-6 text-primary mb-2">
                <i className="fas fa-users"></i>
              </div>
              <h3 className="card-title">{stats.totalUsers.toLocaleString()}</h3>
              <p className="card-text text-muted">Total Users</p>
              {stats.usersLastWeek > 0 && (
                <small className="text-success">
                  <i className="fas fa-arrow-up"></i> +{stats.usersLastWeek} this week
                </small>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
          <div className="card text-center h-100">
            <div className="card-body">
              <div className="display-6 text-info mb-2">
                <i className="fas fa-sticky-note"></i>
              </div>
              <h3 className="card-title">{stats.totalNotes.toLocaleString()}</h3>
              <p className="card-text text-muted">Total Notes</p>
              {stats.notesLastWeek > 0 && (
                <small className="text-success">
                  <i className="fas fa-arrow-up"></i> +{stats.notesLastWeek} this week
                </small>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
          <div className="card text-center h-100">
            <div className="card-body">
              <div className="display-6 text-success mb-2">
                <i className="fas fa-tv"></i>
              </div>
              <h3 className="card-title">{stats.totalDisplays.toLocaleString()}</h3>
              <p className="card-text text-muted">Total Displays</p>
            </div>
          </div>
        </div>

        <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
          <div className="card text-center h-100">
            <div className="card-body">
              <div className="display-6 text-success mb-2">
                <i className="fas fa-play-circle"></i>
              </div>
              <h3 className="card-title">{stats.activeDisplays.toLocaleString()}</h3>
              <p className="card-text text-muted">Active Displays</p>
              <small className="text-muted">
                {stats.totalDisplays > 0 
                  ? `${Math.round((stats.activeDisplays / stats.totalDisplays) * 100)}% active`
                  : 'No displays'
                }
              </small>
            </div>
          </div>
        </div>

        <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
          <div className="card text-center h-100">
            <div className="card-body">
              <div className="display-6 text-warning mb-2">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <h3 className="card-title">{stats.pendingModeration.toLocaleString()}</h3>
              <p className="card-text text-muted">Pending Moderation</p>
              {stats.pendingModeration > 0 && (
                <small className="text-warning">
                  Requires attention
                </small>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
          <div className="card text-center h-100">
            <div className="card-body">
              <div className="display-6 text-secondary mb-2">
                <i className="fas fa-chart-line"></i>
              </div>
              <h3 className="card-title">
                {stats.displayActivity.reduce((sum, item) => sum + item.sessionCount, 0).toLocaleString()}
              </h3>
              <p className="card-text text-muted">Total Sessions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="row mb-4">
        <div className="col-lg-8 mb-4">
          {getRegionalActivityChartData() && (
            <AnalyticsChart
              title="Regional Activity Distribution"
              type="bar"
              data={getRegionalActivityChartData()!}
              height={400}
            />
          )}
        </div>

        <div className="col-lg-4 mb-4">
          {getGrowthChartData() && (
            <AnalyticsChart
              title="Platform Growth Metrics"
              type="pie"
              data={getGrowthChartData()!}
              height={400}
            />
          )}
        </div>
      </div>

      {/* Regional Activity Table */}
      {stats.displayActivity && stats.displayActivity.length > 0 && (
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Regional Activity Details</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Region</th>
                        <th>Displays</th>
                        <th>Notes</th>
                        <th>Sessions</th>
                        <th>Avg Notes/Display</th>
                        <th>Avg Sessions/Display</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.displayActivity.map((region, index) => (
                        <tr key={index}>
                          <td className="fw-bold">{region.region}</td>
                          <td>{region.displayCount.toLocaleString()}</td>
                          <td>{region.noteCount.toLocaleString()}</td>
                          <td>{region.sessionCount.toLocaleString()}</td>
                          <td>
                            {region.displayCount > 0 
                              ? (region.noteCount / region.displayCount).toFixed(1)
                              : '0'
                            }
                          </td>
                          <td>
                            {region.displayCount > 0 
                              ? (region.sessionCount / region.displayCount).toFixed(1)
                              : '0'
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Quick Actions</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3 mb-2">
                  <a href="/admin/users" className="btn btn-outline-primary w-100">
                    <i className="fas fa-users me-2"></i>
                    Manage Users
                  </a>
                </div>
                <div className="col-md-3 mb-2">
                  <a href="/moderator/queue" className="btn btn-outline-warning w-100">
                    <i className="fas fa-clipboard-list me-2"></i>
                    Moderation Queue
                  </a>
                </div>
                <div className="col-md-3 mb-2">
                  <a href="/admin/activity" className="btn btn-outline-info w-100">
                    <i className="fas fa-history me-2"></i>
                    Activity Logs
                  </a>
                </div>
                <div className="col-md-3 mb-2">
                  <button className="btn btn-outline-secondary w-100" onClick={fetchStats}>
                    <i className="fas fa-sync-alt me-2"></i>
                    Refresh Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 